/**
 * lib/observability.ts — ce qui a le droit de sortir vers le collecteur d'erreurs.
 *
 * OGRYN manipule des codes de prise en charge, des pièces d'identité, des IBAN
 * et des adresses de particuliers. Un rapport d'erreur part chez un tiers, en
 * clair, sans que personne ne le relise : c'est le canal d'exfiltration le plus
 * discret d'une application, parce qu'il est réputé technique.
 *
 * CE QUI A ÉTÉ RETIRÉ LE 01/09, et pourquoi il ne faut pas le remettre :
 *
 *   · `Sentry.replayIntegration()` — 10 % de TOUTES les sessions étaient
 *     enregistrées en vidéo, et 100 % de celles qui plantaient. Le collecteur
 *     détenait donc des films de clients lisant leur code à voix haute et de
 *     garagistes déposant leur carte d'identité. Ce n'était pas un réglage trop
 *     large : c'est une collecte qui n'a pas sa place ici (décision D2ter).
 *   · `sendDefaultPii: true` — adresses IP, cookies et identifiants partaient
 *     avec chaque événement.
 *   · `tracesSampleRate: 1` — 100 % des transactions, sans échantillonnage.
 *
 * Le DSN est passé par l'environnement. Vide, le SDK ne s'initialise pas et
 * n'envoie rien : c'est le comportement voulu par défaut, y compris en
 * développement.
 */

/** Vide ⇒ aucun envoi. Un défaut silencieux vaut mieux qu'un DSN en dur. */
export const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN ?? '';

/**
 * Échantillonnage des traces. À 1, chaque requête produit une transaction :
 * volume inutile, et autant d'occasions d'emporter un paramètre sensible.
 */
export const TRACES_SAMPLE_RATE =
  process.env.NODE_ENV === 'production' ? 0.05 : 0.2;

/**
 * Motifs masqués partout où du texte libre part vers le collecteur.
 *
 * Volontairement larges : un faux positif coûte un message d'erreur moins
 * précis, un faux négatif expose une donnée réelle. Le compromis n'est pas
 * symétrique.
 */
const MOTIFS: [RegExp, string][] = [
  // Code de prise en charge / restitution : six chiffres isolés.
  [/(?<![\d.,])\d{6}(?![\d.,])/g, '[code]'],
  // IBAN — deux lettres, deux chiffres, puis jusqu'à 30 alphanumériques.
  [/\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/g, '[iban]'],
  // SIRET (14) et SIREN (9), avec ou sans espaces.
  [/\b\d{3}[ ]?\d{3}[ ]?\d{3}[ ]?\d{5}\b/g, '[siret]'],
  // Carte bancaire.
  [/\b(?:\d[ -]?){13,19}\b/g, '[carte]'],
  // Adresse électronique.
  [/\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g, '[email]'],
  // Téléphone français.
  [/\b(?:\+33|0)[1-9](?:[ .-]?\d{2}){4}\b/g, '[tel]'],
  // Jeton porteur ou clé d'API dans un message.
  [/\b(?:Bearer|sk_live|sk_test|whsec)[_ ][\w-]{8,}/gi, '[secret]'],
];

/** Clés dont la VALEUR ne sort jamais, quel que soit son contenu. */
const CLES_INTERDITES = /^(authorization|cookie|set-cookie|x-api-key|password|token|secret|checkin_code|completion_code|siret|iban|address|adresse)$/i;

export function masquerTexte(s: string): string {
  return MOTIFS.reduce((acc, [motif, par]) => acc.replace(motif, par), s);
}

/**
 * Parcourt l'événement et masque en profondeur. La profondeur est bornée : un
 * événement Sentry peut contenir des cycles, et une sonde qui fait tomber le
 * processus qu'elle observe est pire que pas de sonde du tout.
 */
function masquer(valeur: unknown, profondeur = 0): unknown {
  if (profondeur > 8) return '[profondeur]';
  if (typeof valeur === 'string') return masquerTexte(valeur);
  if (Array.isArray(valeur)) return valeur.map((v) => masquer(v, profondeur + 1));
  if (valeur && typeof valeur === 'object') {
    const sortie: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(valeur as Record<string, unknown>)) {
      sortie[k] = CLES_INTERDITES.test(k) ? '[retiré]' : masquer(v, profondeur + 1);
    }
    return sortie;
  }
  return valeur;
}

/**
 * Dernier filtre avant l'envoi. Il agit sur l'événement RÉEL, pas sur la
 * configuration : c'est le seul endroit dont on peut prouver le contenu.
 */
export function beforeSend<T>(event: T): T {
  const e = event as unknown as Record<string, unknown>;

  // Le corps d'une requête n'aide jamais à diagnostiquer, et contient tout.
  const req = e.request as Record<string, unknown> | undefined;
  if (req) {
    delete req.data;
    delete req.cookies;
    if (req.headers && typeof req.headers === 'object') {
      for (const k of Object.keys(req.headers as Record<string, unknown>)) {
        if (CLES_INTERDITES.test(k)) delete (req.headers as Record<string, unknown>)[k];
      }
    }
    // L'URL porte souvent les paramètres de recherche — donc une adresse.
    if (typeof req.query_string === 'string') req.query_string = '[retiré]';
    if (typeof req.url === 'string') req.url = (req.url as string).split('?')[0];
  }

  // L'identifiant interne suffit à retrouver un compte côté base. L'IP, le
  // courriel et le nom n'apportent rien de plus au diagnostic.
  const user = e.user as Record<string, unknown> | undefined;
  if (user) {
    e.user = { id: user.id };
  }

  return masquer(e, 0) as unknown as T;
}
